import Head from "next/head";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useCart } from "@context/CartContext";
import BrochureLeadModal from "@/components/common/BrochureLeadModal";

export default function ThreeDayImmersive() {
    const router = useRouter();
    const { addToCart } = useCart();
    const [program, setProgram] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);

    useEffect(() => {
        fetchProgramData();
    }, []);

    const fetchProgramData = async () => {
        try {
            // Fetch program details
            const programResponse = await fetch("/api/programs/full-leadership");
            const programResult = await programResponse.json();

            if (programResult.success) {
                const fullProgram = programResult.data;

                // Filter formats to only show 3-Day Immersive
                // Assuming the format id or name contains 'immersive' or '3-day'
                const immersiveFormat = fullProgram.formats?.find(
                    f => f.name.toLowerCase().includes('immersive') || f.subTitle?.toLowerCase().includes('immersive')
                );

                setProgram({
                    ...fullProgram,
                    title: "3-Day Immersive Leadership Program",
                    description: "An intensive, distraction-free leadership journey designed to accelerate your growth over three powerful days.",
                    formats: immersiveFormat ? [immersiveFormat] : fullProgram.formats
                });

                // Fetch schedules for this program
                const schedulesResponse = await fetch("/api/schedules");
                const schedulesResult = await schedulesResponse.json();

                if (schedulesResult.success) {
                    const programSchedules = schedulesResult.data.filter(
                        (s) => s.programSlug === "full-leadership" &&
                            (immersiveFormat ? s.formatId === immersiveFormat.id : true)
                    );
                    setSchedules(programSchedules);
                }
            } else {
                setError("Failed to load program information");
            }
        } catch (err) {
            console.error("Error fetching program data:", err);
            setError("Failed to load program information. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleBuyNow = (format) => {
        if (!program) return;

        // Default to first format if none provided
        const selectedFormat = format || (program.formats && program.formats[0]);

        if (selectedFormat) {
            addToCart({
                programId: program._id,
                formatId: selectedFormat.id,
                title: selectedFormat.name,
                price: selectedFormat.price || 0,
                featuredImg: program.featuredImg,
            });
            router.push('/checkout');
        }
    };

    const curriculumIntro = "The 3-Day Immersive Leadership Experience is crafted as a step-by-step learning journey. Each day builds upon the last, guiding participants from personal leadership awareness to effectively leading teams and ultimately achieving strategic results within their organisations.";

    const learningExperience = [
        "Interactive leadership discussions",
        "Practical simulations and exercises",
        "Peer collaboration and shared learning",
        "Real-world leadership applications",
    ];

    const modules = [
        {
            step: "Day 1",
            title: "Leadership Foundations",
            description: "Build the personal leadership awareness needed for stronger decisions, presence, and consistency.",
            items: [
                "Personal leadership awareness",
                "Core leadership foundations",
                "Mindset for sustained growth",
            ],
            icon: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
            ),
            bgColor: "bg-brand-primary/5",
        },
        {
            step: "Day 2",
            title: "Leading People & Teams",
            description: "Shift from self-leadership to leading others through communication, trust, and team effectiveness.",
            items: [
                "People leadership in practice",
                "Team collaboration and engagement",
                "Leadership communication in group settings",
            ],
            icon: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
            ),
            bgColor: "bg-purple-50",
        },
        {
            step: "Day 3",
            title: "Strategic Leadership",
            description: "Connect leadership behaviour to organisational outcomes and long-term strategic impact.",
            items: [
                "Strategic thinking and alignment",
                "Leading for results",
                "Applying leadership to organisational priorities",
            ],
            icon: (
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
            ),
            bgColor: "bg-teal-50",
        },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
            </div>
        );
    }

    if (error || !program) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error || "Program not found"}</p>
                    <Link
                        href="/programs"
                        className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md"
                    >
                        Back to Programs
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Head>
                <title>3-Day Immersive | L.I.S.T Learning Pathways</title>
                <meta
                    name="description"
                    content="An intensive, 3-day immersive leadership development program."
                />
            </Head>

            <main className="min-h-screen bg-white">
                {/* Hero */}
                <section className="relative overflow-hidden bg-brand-dark pt-12 pb-12 lg:pt-28 lg:pb-20">
                    <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                            <div>
                                <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-base font-bold rounded-full mb-4">
                                    Intensive Learning
                                </span>
                                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                                    {program.title}
                                </h1>
                                <p className="text-lg text-gray-300 max-w-xl leading-relaxed mb-8">
                                    {program.description}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setIsBrochureModalOpen(true)}
                                    className="inline-block px-8 py-4 bg-white border-2 border-white text-brand-primary font-bold rounded-md hover:bg-brand-primary hover:border-brand-primary hover:text-white transition-all active:scale-95 text-center"
                                >
                                    Download Brochure
                                </button>
                            </div>
                            <div className="mt-0 lg:mt-0">
                                <div className="aspect-[4/3] relative rounded-lg overflow-hidden shadow-xl group">
                                    <img
                                        src={program.featuredImg || "/program/p-1.jpg"}
                                        alt={program.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-brand-dark/20 group-hover:bg-brand-dark/10 transition-colors"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Format Details */}
                <section className="py-16 lg:py-20 bg-gray-50/50">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="text-center mb-12">
                            <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-base font-bold rounded-full mb-3">
                                Program Details
                            </span>
                            <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">
                                About the 3-Day Immersive
                            </h2>
                        </div>

                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 mb-12">
                                <div className="grid md:grid-cols-2 gap-8 items-center">
                                    <div id="enroll">
                                        <h3 className="text-2xl font-bold text-brand-dark mb-4">Why choose the Immersive format?</h3>
                                        <ul className="space-y-4">
                                            {[
                                                "Continuous learning cycle without distractions",
                                                "Deep networking with fellow participants",
                                                "Immediate application of concepts",
                                                "High-energy environment for transformation",
                                                "Complete the L.I.S.T journey in one intensive stretch"
                                            ].map((item, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <div className="w-5 h-5 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                                        <svg className="w-3 h-3 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <span className="text-gray-600">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="bg-brand-dark/5 p-6 rounded-lg border border-brand-dark/10">
                                        <div className="mb-6">
                                            <span className="text-sm text-gray-500 block mb-1">Tuition</span>
                                            <span className="text-4xl font-black text-brand-dark">
                                                {program.formats[0]?.price > 0 ? `₹${program.formats[0]?.price.toLocaleString()}` : "Price Upon Request"}
                                            </span>
                                            <p className="text-[10px] text-gray-400 font-medium mt-1 leading-none">(Inclusive of 18% GST)</p>
                                        </div>
                                        <div className="space-y-3 mb-8">
                                            <div className="flex justify-between text-sm py-2 border-b border-gray-100">
                                                <span className="text-gray-500">Duration</span>
                                                <span className="font-bold text-brand-dark">{program.formats[0]?.duration || "3 Days"}</span>
                                            </div>
                                            <div className="flex justify-between text-sm py-2 border-b border-gray-100">
                                                <span className="text-gray-500">Format</span>
                                                <span className="font-bold text-brand-dark">On-site / In-person</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                            {program.formats[0]?.price > 0 && (
                                                <button
                                                    onClick={() => handleBuyNow(program.formats[0])}
                                                    className="block w-full text-center py-4 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95"
                                                >
                                                    Enroll Now
                                                </button>
                                            )}
                                            <Link
                                                href={`/enrollment?program=${program.slug}&format=${program.formats[0]?.id}&intent=advisor`}
                                                className="block w-full text-center py-4 bg-gray-50 text-gray-700 border border-gray-200 font-bold rounded-md hover:bg-gray-100 transition-all"
                                            >
                                                Enroll with Advisor
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Upcoming Sessions */}
                {schedules.length > 0 && (
                    <section className="py-16 lg:py-20 bg-white">
                        <div className="max-w-7xl mx-auto px-6 lg:px-12">
                            <div className="text-center mb-12">
                                <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">
                                    Upcoming 3-Day Batches
                                </h2>
                            </div>

                            <div className="max-w-4xl mx-auto space-y-4">
                                {schedules.map((schedule) => (
                                    <div
                                        key={schedule.id}
                                        className="flex flex-col md:flex-row justify-between items-center p-6 bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-xl hover:border-brand-primary/20 transition-all duration-300"
                                    >
                                        <div className="flex items-center gap-4 mb-4 md:mb-0">
                                            <div className="w-16 h-16 bg-brand-primary/10 rounded-md flex flex-col items-center justify-center shrink-0">
                                                <span className="text-xs font-bold text-brand-primary">{schedule.month}</span>
                                                <span className="text-xl font-black text-brand-dark">{schedule.day}</span>
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-bold text-brand-dark mb-1">{schedule.title}</h4>
                                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                                    <span>{schedule.time}</span>
                                                    <span className="mx-2">•</span>
                                                    <span>{schedule.location}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col md:flex-row gap-2">
                                            {program.formats?.find(f => f.id === schedule.formatId)?.price > 0 && (
                                                <button
                                                    onClick={() => handleBuyNow(program.formats?.find(f => f.id === schedule.formatId))}
                                                    className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95"
                                                >
                                                    Book Now
                                                </button>
                                            )}
                                            <Link
                                                href={`/enrollment?program=${program.slug}&format=${schedule.formatId}&date=${new Date(schedule.date).toISOString()}&intent=advisor`}
                                                className="px-6 py-3 bg-gray-50 text-gray-700 border border-gray-200 font-bold rounded-md hover:bg-gray-100 transition-all text-center"
                                            >
                                                Register
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Modules */}
                <section className="py-16 lg:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">
                                Curriculum Focus
                            </h2>
                            <p className="max-w-3xl mx-auto mt-4 text-base lg:text-lg text-gray-500 leading-relaxed">
                                {curriculumIntro}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                            <div className="bg-brand-dark text-white rounded-2xl p-8 lg:p-10 shadow-xl relative overflow-hidden lg:sticky lg:top-24 self-start">
                                <div className="absolute inset-0 bg-brand-primary/10"></div>
                                <div className="relative z-10">
                                    <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-[0.18em] rounded-full mb-4">
                                        3-Day Immersive
                                    </span>
                                    <h3 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                                        A guided progression from self-awareness to strategic leadership
                                    </h3>
                                    <p className="text-gray-300 leading-relaxed mb-6">
                                        Each day builds on the last so participants move from understanding themselves as leaders,
                                        to leading people and teams effectively, and finally to applying leadership in ways that
                                        drive organisational outcomes.
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        {learningExperience.map((item, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/10 text-sm text-white"
                                            >
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grid gap-6">
                                {modules.map((m, i) => (
                                    <div
                                        key={i}
                                        className="bg-white p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300"
                                    >
                                        <div className={`w-12 h-12 ${m.bgColor} rounded-md flex items-center justify-center mb-4`}>
                                            <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                {m.icon}
                                            </svg>
                                        </div>
                                        <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-brand-primary mb-2">
                                            {m.step}
                                        </span>
                                        <h5 className="text-lg font-bold mb-3 text-brand-dark">{m.title}</h5>
                                        <p className="text-sm text-gray-500 leading-relaxed mb-4">{m.description}</p>
                                        <ul className="space-y-2">
                                            {m.items.map((item, idx) => (
                                                <li key={idx} className="flex items-start gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-brand-primary mt-2 shrink-0"></div>
                                                    <span className="text-gray-500 text-sm leading-tight">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 lg:py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="bg-brand-dark rounded-lg p-12 text-center text-white relative overflow-hidden shadow-xl">
                            <div className="relative z-10 max-w-2xl mx-auto">
                                <h2 className="text-3xl md:text-4xl font-extrabold mb-4 tracking-tight">
                                    Ready for the <span className="text-brand-primary">Immersive</span> Experience?
                                </h2>
                                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                                    {program.formats && program.formats[0]?.price > 0 && (
                                        <button
                                            onClick={() => handleBuyNow()}
                                            className="px-8 py-4 bg-brand-primary text-white font-bold rounded-md hover:shadow-lg hover:shadow-brand-primary/30 transition-all active:scale-95"
                                        >
                                            Enroll Now
                                        </button>
                                    )}
                                    <Link
                                        href="/enrollment"
                                        className="px-8 py-4 bg-white/10 text-white border border-white/20 font-bold rounded-md hover:bg-white/20 transition-all active:scale-95 text-center"
                                    >
                                        Enroll Now
                                    </Link>
                                    <Link
                                        href="/contact"
                                        className="px-8 py-4 bg-transparent border-2 border-white/20 text-white font-bold rounded-md hover:bg-white/10 hover:border-white transition-all text-center"
                                    >
                                        Contact Us
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <BrochureLeadModal
                isOpen={isBrochureModalOpen}
                onClose={() => setIsBrochureModalOpen(false)}
                brochureUrl="/brochure/LIST%20Leadership.pdf"
                programId={program?._id}
                programName={program?.title || "3-Day Immersive Leadership Program"}
                format={program?.formats?.[0]?.name || "3-Day Immersive"}
            />
        </>
    );
}
